import { PageControl } from './page-control';
import { Params } from '@angular/router';
import { MolicarPrice } from './molicar';
import { VehicleFullDossier } from './dossier';
import { AuctionHistoryEvent, EquipmentReport, FipePrice } from './vehicle';
import { CounterOffer } from '@app-services/dealer.service';
import { Injectable } from "@angular/core";
import { AudioHelper } from '@app/views/public/plugin-raio-x/plugin-raio-x.service';
import { EvalLeadsResponse } from './eval-lead';
import { RaioXPluginConfigItem } from '@app/views/private/admin/model/advertisers';
import { IdName } from '@app-services/oem-dealer.service';
import type { EvalVehicleRescueDetails } from './eval-rescue';

const OBJECT_KEY_NAMES = [
  'evalDealerIds',
  'listView',
  'oemDealerId',
  'oemDealerIds',
  'statuses',
  'searchQuery',
  'segments',
  'evalTypes',
  'ownerTypes',
  'ufs',
  'cityIds',
  'rescueStatuses'
];
const NUMBER_KEY_NAMES = ['page'];
const BOOLEAN_KEY_NAMES = ["hasIntegrationError", "inFastQuote"];
const IGNORED_KEY_NAMES = ['pageControl'];
const PAGE_CONTROL_KEY_NAMES = ['page', 'sortField', 'sortDirection'];
export class EvaluationVehicleFilter {
  evalDealerIds: string[];
  listView: string;
  oemDealerId: string;
  oemDealerIds: string[];
  excludeOemDealerIds?: string[];
  pageControl: PageControl;
  searchQuery: string;
  segments: string[];
  statuses: string[];
  evalTypes: string[];
  ownerTypes: string[];
  gridMode?: boolean;

  createdAtIni: Date;
  createdAtEnd: Date;
  updatedAtIni: string;
  updatedAtEnd: string;
  makeIds: string[];
  modelIds: string[];
  cityIds: string[];
  ufs: string[];
  destinations: string[];
  scores: number[];
  types: string[];
  regionalIds: string[];
  evaluatorIds: string[];
  sellerIds: string[];
  pricingUserIds: string[];
  sellerManagerUserIds: string[];
  negotiationStatuses: string[];
  modelYearStart: number;
  modelYearEnd: number;
  fastQuoteStatuses: string[];
  rescueStatuses?: string[];
  fipeDiffRanges: string[];
  priceComparison: string[];
  purchasePriceRanges: string[];
  priceType: string;
  hasIntegrationError?: boolean;
  inFastQuote?: boolean;
  evalDealerDivisions: string[];
  evalDealerMakeIds: string[];
  boughtAtIni: any;
  boughtAtEnd: any;
  marginPercentageIni: number;
  marginPercentageEnd: number;
  onlyInInventory: boolean;
  onlyTradeIn: boolean;
  noTradeInOnly: boolean;
  pricingEndedAtIni: any;
  pricingEndedAtEnd: any;
  marketModality: string;

  constructor() {
    this.evalDealerIds = [];
    this.listView = 'SELLER';
    this.oemDealerId = null;
    this.oemDealerIds = [];
    this.pageControl = {
      page: 0,
      pageSize: 20,
      param: '',
      sortDirection: 'DESC',
      sortField: 'updatedAt',
      value: {},
    };
    this.searchQuery = '';
    this.segments = [];
    this.statuses = [];
    this.negotiationStatuses = [];
    this.rescueStatuses = [];
    this.priceComparison = [];
  }

  toParamMap(): Params {
    const params: Params = {};
    Object.keys(this).forEach((k) => {
      let value: any;
      if (OBJECT_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.stringify(this[k]);
      } else if (IGNORED_KEY_NAMES.indexOf(k) < 0) {
        value = this[k];
      }
      params[k] = value;
    });

    params.page = this.pageControl.page;
    params.sortField = this.pageControl.sortField;
    params.sortDirection = this.pageControl.sortDirection;
    return params;
  }

  updateFromParamMap(params: Params): void {
    Object.keys(params).forEach((k) => {
      let value: any;
      if (OBJECT_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.parse(params[k]);
      } else if (IGNORED_KEY_NAMES.indexOf(k) < 0) {
        value = params[k];
      }

      if (NUMBER_KEY_NAMES.indexOf(k) >= 0) {
        value = Number(value);
        if (value === Number.NaN) {
          value = undefined;
        }
      } else if (BOOLEAN_KEY_NAMES.indexOf(k) >= 0) {
        value = JSON.parse(value);
      }

      if (value !== undefined) {
        if (PAGE_CONTROL_KEY_NAMES.indexOf(k) >= 0) {
          this.pageControl[k] = value;
        } else {
          this[k] = value;
        }
      }
    });
  }
}

export interface EvaluationVehicleResponse {
  numberOfPages: number;
  actualPage: number;
  totalElements: number;
  data: EvaluationVehicle[];
}

export interface EvaluatorOption {
  id: string;
  name: string;
  hasCpf: boolean;
}

export interface EvaluationLeadResponse {
  numberOfPages: number;
  actualPage: number;
  totalElements: number;
  data: Map<string, []>[];
}

export interface EvaluationPhoto {
  url: string;
  type: string;
}


export enum StatusEvaluation {
  CREATED = 'CREATED',
  EVALUATING = 'EVALUATING',
  SCHEDULED = 'SCHEDULED',
  WAITING = 'WAITING',
  RETURNED_EVAL = 'RETURNED_EVAL',
  PRICING = 'PRICING',
  NEGOTIATION = 'NEGOTIATION',
  BOUGHT = 'BOUGHT',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
  ARCHIVED = 'ARCHIVED',
  FAST_QUOTE = 'FAST_QUOTE',
  FAST_QUOTE_WITH_OFFER = 'FAST_QUOTE_WITH_OFFER',
  SOLD = 'SOLD',
  SOLD_FAST_QUOTE = 'SOLD_FAST_QUOTE',
  DELETED = 'DELETED',
  AWAITING_ENTRY = 'AWAITING_ENTRY',
  RETURNED_PRICING = 'RETURNED_PRICING'
}

export class Owner {
  cpf: string;
  email: string;
  name: string;
  phoneNumber: string;
  rg: string;
  cpfLabel: string;
}

export class EvalQuestions {
  dateLastRevision?: string;
  involvedInAccident?: string;
  primaryUse?: string;
  retouchedBodywork?: string;
  vehicleOwnership?: string;
  revisionInDealership?: string;
  odometerReplacement?: string;
  maintenanceBudget?: string;
  estimatedMarketValue?: string;
}

export class Address {
  address: string;
  zipCode: string;
  addressNumber: string;
  addressComplement: string;
  district: string;
  cityName: string;
  uf: string;
}

export class AxleAndTire {
  axles: Axle[];
  stepQuantity: number;
  tiresDimension: number;
  wheelbase: number;
}

export type EvalVehicleSegment = 'CAR' | 'MOTORCYCLE' | 'TRUCK';

export function resolveEvaluationVehicleSegment(
  vehicle?: Partial<EvaluationVehicle>,
  formSegment?: string | null
): EvalVehicleSegment {
  const explicitSegment = vehicle?.segment || formSegment;
  if (explicitSegment === 'TRUCK' || explicitSegment === 'MOTORCYCLE' || explicitSegment === 'CAR') {
    return explicitSegment;
  }

  const hasTruckData = !!(
    vehicle?.truckCabin ||
    vehicle?.truckType ||
    vehicle?.truckClassification ||
    vehicle?.truckTraction ||
    vehicle?.truckCabinLabel ||
    vehicle?.truckClassificationLabel ||
    vehicle?.truckTractionLabel ||
    vehicle?.truckTypeLabel
  );

  if (hasTruckData) {
    return 'TRUCK';
  }

  if (vehicle?.motorcycleType) {
    return 'MOTORCYCLE';
  }

  return 'CAR';
}

export class EvaluationVehicle {
  allowDirectSale?: boolean;
  acceptedTermsDatetime?: Date;
  applicantId?: string;
  applicantName?: string;
  currentEvaluatorId?: string;
  currentEvaluatorName?: string;
  axleAndTire: AxleAndTire;
  bankSlipUrl?: string;
  bestBid?: number;
  bodywork?: string;
  boughtAt?: Date;
  buyPrice?: BuyPriceSummary;
  canChangeEvalDealer?: boolean;
  carManual?: boolean;
  chassis?: string;
  city?: string;
  cityId?: string;
  color?: string;
  comments?: EvalComment[];
  commentsForPricing?: PricingComment[];
  evalComments?: EvalComment[];
  rescueId?: string;
  rescueComments?: EvalComment[];
  rescue?: EvalVehicleRescueDetails | null;
  countryOrigin?: string;
  createdAt?: Date;
  description?: string;
  destination?: EvaluationDestination;
  doors?: number;
  dossier?: VehicleFullDossier;
  dossierPrice?: number;
  dossierStatus?: string;
  equipmentIds?: string[];
  originalEquipmentIds?: string[];
  originalEquipmentsByFipeId?: { [fipeId: string]: string[] };
  evalDealerAddress?: Address;
  evalDealerId?: string;
  evalDealerName?: string;
  evalPercentage?: number;
  evalReason?: string;
  expectedPrice?: number;
  expirationTimeLeft: number;
  extraKey?: boolean;
  factoryBonus?: number;
  interventionPrice?: number;
  fastQuote?: FastQuote;
  fastQuoteExpiration?: Date;
  fastQuoteId?: string;
  inFastQuote?: boolean;
  finalEvalScore?: number;
  finalEvalScoreABC?: string;
  fipePrice?: number;
  futureFipePrice?: number;
  currentFipePrice?: number;
  fuel?: string;
  fullDossierDate?: Date;
  hasBids?: boolean;
  hasImgs?: boolean;
  hasTradeIn?: boolean;
  hasVideo?: boolean;
  historyEvents?: AuctionHistoryEvent[];
  negotiationClosedAt: Date;
  agreedPrice: number;
  showAwaitingEntryActions: boolean;
  id?: string;
  images?: EvaluationPhoto[];
  imgUrl?: string;
  imgUrls?: string[];
  implement: Implement;
  includeExtraQuestionsInEval?: boolean;
  interestedAt?: string;
  gearsSize: number;
  km?: number;
  lastHistoryItem?: LastHistoryItem;
  leadPrice: number;
  leadPriceExpirationDate: Date;
  listPrice?: number;
  makeId?: string;
  makeName?: string;
  manufactureYear?: number;
  minSellPrice?: number;
  modelId?: string;
  modelName?: string;
  modelYear?: number;
  molicarPrices?: MolicarPrice[];
  negotiationStatus?: NegotiationStatus;
  numberPlate?: string;
  numHistoryItems?: number;
  numImages?: number;
  numIntegrationHistoryItems?: number;
  oemDealer?: string;
  oemDealerId?: string;
  onlineEvaluationLink?: string;
  otherEvalReason?: string;
  otherEvalsSameVehicle?: EvaluationVehicle[];
  owner?: Owner = new Owner();
  passengers?: number;
  pluginRaioXPrice?: number;
  power?: number;
  price?: number;
  priceCent?: number;
  tradeInMarketPrice?: number;
  tradeInDealerCosts?: number;
  estimatedPrice?: boolean;
  questions: EvalQuestions;
  raioXPluginConfigItem: RaioXPluginConfigItem;
  reasonArchive?: string;
  recentHistoryItems?: RecentHistoryItem[];
  recentIntegrationHistoryItems?: IntegrationHistoryItem[];
  registrationCity?: string;
  reportUrl?: string;
  reportTitle?: string;
  reportItems?: any;
  reportStatus?: any;
  salesEvent?: boolean;
  scheduledDate: Date;
  segment?: string;
  sellPrice?: SellPriceSummary;
  stats?: FastQuoteStats;
  status?: StatusEvaluation;
  target?: string;
  tankSize: number;
  tireReports: TireReport[];
  totalEvalFilled : number;
  transmission?: string;
  transmissionLabel: string;
  trimId?: string;
  trimName?: string;
  truckCabin: string;
  truckCabinLabel: string;
  truckClassification?: string;
  truckClassificationLabel: string;
  truckTraction?: string;
  truckTractionLabel: string;
  truckType?: string;
  truckTypeLabel: string;
  type: string; // 'OFFLINE', 'ONLINE
  uf?: string;
  useEvalScoreABC?: boolean;
  userNameLastEvalHistoryItem?: string;
  renavam?: string;
  oldDossiers: DossierHelper[];
  evolution: EvalEvolution;
  expirationDate: Date;
  audio: AudioHelper;
  createdOnline: boolean;
  blockEvalReturnToDeskBuy: boolean;
  canEditPrice?: boolean;
  motorcycleType: MotorcycleType;
  oldFipePrices: FipePrice[];
  externalKeys: ExternalKey[];
  webAverage: WebAveragePrice;
  webPrice: number;
  webMotorsDetails?: WebMotorsDetails;
  icarrosDetails?: ICarrosDetails;
  numberPlateOfInterest: string;
  fillPriceBeforePurchase: boolean;
  defaultCompetitiveFastQuote: boolean;
  stockDays: number;
  tradeInVehicleId?: string;
  grossMargin?: number;
  margin?: number;
  pricingUserId?: string;
  pricingUserName?: string;
  blockViewEvalWithoutAssume?: boolean;
  firstPricingUserId?: string | null;
  firstPricingUserName?: string | null;
  pricingStartedAt?: Date;
  pricingEndedAt?: Date;
  pricingDurationMinutes?: number;
  lastDeskPrice?: number;

  get trim() {
    return this.trimName;
  }

  get make() {
    return this.makeName;
  }

  get model() {
    return this.modelName;
  }

  get trimWithNoModel(): string {
    if (this.trim.startsWith(this.model + ' ')) {
      return this.trim.substring(this.model.length + 1);
    } else {
      return this.trim;
    }
  }
}

export class PricingComment {
  comment: string;
  createdAt: Date;
  userId: string;
  username: string;
}

export interface EvalComment {
  comment: string;
  createdAt: Date;
  userId: string;
  username: string;
  private: boolean;
  rescue?: boolean;
  rescueId?: string;
  edited?: boolean;
  editedAt?: Date;
}

export interface ExternalKey {
  provider: string;
  externalKey: any;
}

export interface EvalOptions {
  makeOptions: IdName[];
  transmissionOptions: IdName[];
  reportEquipmentOptions: IdName[];
  reportPhotoOptions: IdName[];
  equipmentOptions: IdName[];
}

export interface EvalMotorcycleOptions extends EvalOptions {
  motorcycleTypeOptions: IdName[];
}

export interface EvalTruckOptions extends EvalOptions {
  tractionOptions: TractionOptions[];
  classificationsOptions: ClassificationOptions[];
  cabinOptions: IdName[];
  truckOptions: IdName[];
  implementTypeOptions: IdName[];
}

export class Implement {
  type: string;
  typeLabel: string;
  makeId: string;
  makeName: string;
}

export class TractionOptions {
  id: string;
  label: string;
  numberOfAxles: number;
}

export class ClassificationOptions {
  id: string;
  name: string;
  minWeight: number;
}

export interface CaptureTypeOption {
  type: string;
  icon: string;
  raioxLabel: string;
  typeLabel: string;
}

export type EvalType = 'OFFLINE' | 'ONLINE' | 'INTERCOMPANY' | 'REPAIR_SHOP';

export type EvalRequiredField =
  | 'MAKE' | 'MODEL' | 'VERSION' | 'MANUFACTURE_YEAR' | 'MODEL_YEAR'
  | 'CHASSIS' | 'RENAVAM' | 'KM' | 'FUEL' | 'DOORS' | 'COLOR' | 'EXTRA_KEY'
  | 'CAR_MANUAL' | 'ARMORED' | 'POWER' | 'PASSENGERS' | 'TRANSMISSION' | 'COUNTRY_ORIGIN'
  | 'REGISTRATION_CITY' | 'APPLICANT' | 'COMMENTS'
  | 'OWNER_NAME' | 'OWNER_EMAIL' | 'OWNER_PHONE' | 'OWNER_DOCUMENT'
  | 'EXPECTED_PRICE' | 'EVAL_REASON' | 'NUMBER_PLATE_OF_INTEREST' | 'TRADE_IN_VEHICLE'
  | 'REPORT' | 'REPORT_STATUS' | 'INSPECTION_AUDIO'
  | 'MOTORCYCLE_TYPE'
  | 'TRUCK_CABIN' | 'TRUCK_TRACTION' | 'BODYWORK' | 'GEARS_SIZE' | 'TRUCK_TYPE' | 'TRUCK_CLASSIFICATION'
  | 'TANK_SIZE' | 'IMPLEMENT_TYPE' | 'IMPLEMENT_MAKE'
  | 'AXLES' | 'TIRES_DIMENSION' | 'STEP_QUANTITY' | 'WHEELBASE';

export const EVAL_REQUIRED_FIELD_ALIASES: Readonly<Record<string, EvalRequiredField>> = {
  CABIN: 'TRUCK_CABIN',
  TRACTION: 'TRUCK_TRACTION',
  GEARS_COUNT: 'GEARS_SIZE',
  APPLICATION: 'TRUCK_TYPE',
  CLASSIFICATION: 'TRUCK_CLASSIFICATION',
  TANK_CAPACITY: 'TANK_SIZE',
  IMPLEMENT_BRAND: 'IMPLEMENT_MAKE',
  TIRE_DIMENSION: 'TIRES_DIMENSION',
  SPARE_TIRE_COUNT: 'STEP_QUANTITY',
  MILEAGE: 'KM',
  FUEL_TYPE: 'FUEL',
  SPARE_KEY: 'EXTRA_KEY',
  OWNER_MANUAL: 'CAR_MANUAL',
  ARMORED_VEHICLE: 'ARMORED',
  HORSEPOWER: 'POWER',
  ORIGIN: 'COUNTRY_ORIGIN',
  REQUESTER: 'APPLICANT',
  PRICE_EXPECTATION: 'EXPECTED_PRICE',
  CAPTURE_REASON: 'EVAL_REASON',
  PLATE_OF_INTEREST: 'NUMBER_PLATE_OF_INTEREST',
  VEHICLE_OF_INTEREST: 'TRADE_IN_VEHICLE',
  INSPECTION_REPORT: 'REPORT',
  MOTO_TYPE: 'MOTORCYCLE_TYPE',
  CHASSI: 'CHASSIS',
};

export class EvalTypeLabel {
  static allTypes = ['OFFLINE', 'ONLINE', 'INTERCOMPANY', 'REPAIR_SHOP'];

  static captureTypeOptions: CaptureTypeOption[] = [
    { type: 'OFFLINE', icon: 'fa-map-marker-alt', raioxLabel: 'RaioX in-loco', typeLabel: 'Vistoria presencial' },
    { type: 'ONLINE', icon: 'fa-globe', raioxLabel: 'RaioX online', typeLabel: 'Vistoria remota' },
    { type: 'INTERCOMPANY', icon: 'fa-building', raioxLabel: 'Intercompany', typeLabel: 'Vistoria entre empresas' },
    { type: 'REPAIR_SHOP', icon: 'fa-tools', raioxLabel: 'Oficina', typeLabel: 'Prospecção a partir da oficina' },
  ];

  static getLabel(type: string): string {
    switch (type) {
      case 'ONLINE': return 'RaioX online';
      case 'OFFLINE': return 'RaioX in-loco';
      case 'INTERCOMPANY': return 'Vistoria entre empresas';
      case 'REPAIR_SHOP': return 'Oficina';
      default: return type;
    }
  }
}

export class OwnerTypeLabel {
  static allTypes = ['PERSON', 'COMPANY'];

  static getLabel(type: string): string {
    switch (type) {
      case 'PERSON': return 'PF';
      case 'COMPANY': return 'PJ';
      default: return type;
    }
  }
}


export class EvalReason {
  static allEvalReasons = ['EXCHANGE_NEW', 'EXCHANGE_USED', 'BOUGHT_ONLY', 'CONSIGNED', 'FLEET', 'CORPORATE_SELL', 'DIRECT_SELL', 'OTHER',
    'BOUGHT_ONLY_NEW', 'BOUGHT_ONLY_USED', 'BOUGHT_DIRECT_SELL', 'VEHICLE_DIRECT_SELL', 'INTERCOMPANY', 'SALES_EVENT'];

  static getLabel(reason: string): string {
    switch (reason) {
      case 'BOUGHT_ONLY': return 'Somente compra';
      case 'CONSIGNED': return 'Consignado';
      case 'FLEET': return 'Veículo da Frota';
      case 'EXCHANGE_NEW': return 'Troca por novo';
      case 'EXCHANGE_USED': return 'Troca por Seminovo';
      case 'CORPORATE_SELL': return 'Venda Corporativa';
      case 'DIRECT_SELL': return 'Venda Direta';
      case 'OTHER': return 'Outro';
      case 'BOUGHT_ONLY_NEW': return 'Somente Compra Novo';
      case 'BOUGHT_ONLY_USED': return 'Somente Compra Usado';
      case 'BOUGHT_DIRECT_SELL': return 'Compra Venda Direta';
      case 'VEHICLE_DIRECT_SELL': return 'Veículo Venda Direta';
      case 'INTERCOMPANY': return 'Intercompany';
      case 'SALES_EVENT': return 'Feirão';
      default: return reason;
    }
  }

  static isExchange(reason: string): boolean {
    return reason === 'EXCHANGE_NEW' || reason === 'EXCHANGE_USED';
  }

  static isOther(reason: string): boolean {
    return reason === 'OTHER';
  }

  static isDirectSell(reason: string): boolean {
    return reason === 'DIRECT_SELL';
  }
}


export class FastQuote {
  bidSuggestions?: FastQuoteBids[];
  bidStep: number;
  commissionPaidAt?: Date;
  commissionValueCents?: number;
  dealersExclusiveGroups?: string[];
  expiration?: Date;
  expirationTimeLeft?: number;
  fastQuoteId?: string;
  history: FastQuoteHistory[];
  margin?: number;
  minBidValue?: number;
  pendingPayment?: boolean;
  personalizedFinishTime?: string;
  soldAt?: Date;
  stats: FastQuoteStats;
  target: string;
  waitingDecision?: boolean;
  imgUrl: string;
  isAuction: boolean;
}

export class FastQuoteHistory {
  createdAt: Date;
  bid?: number;
  type: string;
  dealerName?: string;
  userName?: string;
}

export class FastQuoteStats {
  bids: number;
  bought: number;
  cancelledBuy: number;
  id: {
    date: Date;
    id: string;
  };
  impressions: number;
  uniqueImpressions: number;
  uniqueViews: number;
  views: number;
}
export class FastQuoteBids {
  createdAt: Date;
  dealerCity: string;
  dealerCpfCnpj: string;
  dealerEmails: string[];
  dealerId: string;
  dealerName: string;
  dealerPhoneNumbers: string[];
  dealerUf: string;
  status: string;
  userId: string;
  value: number;
  counterOffer: CounterOffer;
  rejectionReason: "CUSTOMER_GAVE_UP" | "PRICE_TOO_LOW" | "OTHER_DEALER";
  rejectionReasonMessage: string;
}

export class BuyPriceSummary {
  averageBuyPrice?: number;
}
export class SellPriceSummary {
  averageSellPrice?: number;
  numVehicles?: number;
}

export interface WebAveragePrice {
  minPrice: number;
  avgPrice: number;
  maxPrice: number;
}

export interface WebMotorsDetails {
  minPrice: number;
  avgPrice: number;
  maxPrice: number;
  updatedAt?: Date | string;
}

export interface ICarrosDetails {
  avgPrice: number;
  updatedAt?: Date | string;
}

export interface RecentHistoryItem {
  type: HistoryItemType;
  createdAt?: Date;
  userId?: string;
  userName?: string;
  comments?: string;
  diff?: any[];
  pricingDurationMinutes?: number;
}

export interface IntegrationHistoryItem {
  provider: string;
  integrationType: string;
  type: string;
  fieldTypes: string[];
  createdAt: Date;
  method?: string | null;
  integrationUrl?: string | null;
  requestBody?: string | null;
  response?: string | null;
  showingDetails?: boolean;
}

export enum IntegrationHistoryItemTypeLabel {
  SYNCED = 'Sincronizado com sucesso',
  MISSING_MANDATORY_FIELDS = 'Dados obrigatórios não preenchidos',
  MAPPING_NOT_FOUND = 'Mapeamento não encontrado',
  AUTHENTICATION_ERROR = 'Problemas com a autenticação no fornecedor',
  INTEGRATION_ERROR = 'Não foi possível integrar'
}

export enum IntegrationFieldTypeLabel {
  KM = 'km',
  FUEL = 'Combustível',
  SCORE = 'Nota da avaliação',
  PRICE = 'Preço avaliado',
  LIST_PRICE = 'Preço para anúncio',
  EXPECTED_PRICE = 'Expectativa de preço',
  TRIM = 'Versão',
  MAKE = 'Marca',
  EVAL_DEALER = 'Loja',
  VEHICLE_OWNER = 'Proprietário do veículo',
  VEHICLE_OWNER_DOC = 'Documento do proprietário do veículo',
  SELLER = 'Vendedor',
  EVALUATOR = 'Avaliador',
  EVALUATOR_CPF = 'CPF do Avaliador',
  USER = 'Usuário responsável pela atualização',
  FULL_DOSSIER = '+RaioX (Histórico Veicular)',
  CHASSIS = 'Chassis',
  DOORS = 'Portas',
  RENAVAM = 'Renavam',
  COLOR = 'Cor',
  CREATE_AT = 'Data de criação',
  EXPIRATION_DATE = 'Data de validade',
  EVAL_REASON = 'Motivo da captação',
  VEHICLE_ORIGIN = 'Origem (nacional/importado)',
  FIPE_ID = 'Código FIPE',
  MODEL_YEAR = 'Ano modelo'
}

export class MotorcycleType {

  static allTypes = ["URBAN_RUNWAY", "URBAN_ADVENTURE", "ENDURO_OFF_ROAD", "RACING", "SPORT", "SCOOTER"];

  static getLabel(label: string) {
    switch (label) {
      case 'URBAN_RUNWAY': return "Urbano/Pista";
      case 'URBAN_ADVENTURE': return "Urbano/Aventura";
      case 'ENDURO_OFF_ROAD': return "Enduro/Off-Road";
      case 'RACING': return "Competição";
      case 'URBAN_ADVENTURE': return "nduro/Off-Road";
      case 'SPORT': return "Sport";
      case 'SCOOTER': return "Scooter";
    }
  }

}

interface LastHistoryItem {
  id: string;
  evalVehicleId: string;
  evalDealerId: string;
  type: HistoryItemType;
  createdAt: string;
  userId: string;
  diff: any[];
  comments: string;
}

export enum HistoryItemType {
  CREATED = 'CREATED',
  SCHEDULED = 'SCHEDULED',
  WAITING = 'WAITING',
  SENT_TO_EVAL = 'SENT_TO_EVAL',
  STARTED = 'STARTED',
  CLOSED_EVAL = 'CLOSED_EVAL',
  CLOSED_PRICING = 'CLOSED_PRICING',
  BOUGHT = 'BOUGHT',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
  UPDATED_INITIAL = 'UPDATED_INITIAL',
  UPDATED_EVALUATION = 'UPDATED_EVALUATION',
  UPDATED_PRICING = 'UPDATED_PRICING',
  UPDATED = 'UPDATED',
  RETURNED_EVAL = 'RETURNED_EVAL',
  RETURNED_TO_PRICING = 'RETURNED_TO_PRICING',
  REACTIVATED = 'REACTIVATED',
  CHANGED_APPLICANT = 'CHANGED_APPLICANT',
  CHANGED_EVALUATOR = 'CHANGED_EVALUATOR',
  CHANGED_EVAL_DEALER = 'CHANGED_EVAL_DEALER',
  ARCHIVED = 'ARCHIVED',
  VIEW = 'VIEW',
  STARTED_PRICING = 'STARTED_PRICING',
  ABANDONED_PRICING = 'ABANDONED_PRICING',
  EDITED_EVALUATION_COMMENT = 'EDITED_EVALUATION_COMMENT',
  INTERVENTION_PRICE_ACCEPTED = 'INTERVENTION_PRICE_ACCEPTED',
  ESTIMATED_PRICE = 'ESTIMATED_PRICE',
}

export enum EvaluationStatusSelectedOptions {
  CREATED = 'Não iniciada',
  SCHEDULED = 'Agendada',
  WAITING = 'Aguardando envio',
  EVALUATING = 'RaioX em andamento',
  RETURNED_EVAL = 'RaioX devolvido',
  PRICING = 'Em precificação',
  RETURNED_PRICING = 'Precificação devolvida',
  NEGOTIATION = 'Em negociação',
  AWAITING_ENTRY = 'Aguardando Entrada',
  FAST_QUOTE = 'Em cotação rápida',
  FAST_QUOTE_WITH_OFFER = '',
  // FAST_QUOTE_WITH_OFFER = 'Em cotação rápida (com oferta)'
}

export enum FipeDiffRange {
  LESS_THAN_50 = 'Abaixo de -50%',
  BETWEEN_50_40 = 'Entre -50% e -40%',
  BETWEEN_40_30 = 'Entre -40% e -30%',
  BETWEEN_30_20 = 'Entre -30% e -20%',
  BETWEEN_20_10 = 'Entre -20% e -10%',
  BETWEEN_10_ZERO = 'Entre -10% e 0%',
  POSITIVE = 'Acima da FIPE'
}

export enum MarketPriceRange {
  LESS_THAN_10 = 'Abaixo de -10%',
  BETWEEN_10_05 = 'Entre -10% e -5%',
  BETWEEN_05_02 = 'Entre -5% e -2%',
  BETWEEN_02_ZERO = 'Entre -02% e 0%',
  BETWEEN_ZERO_02 = 'Entre 0% e 2%',
  BETWEEN_02_05 = 'Entre 2% e 5%',
  BETWEEN_05_10 = 'Entre 5% e 10%',
  MORE_THAN_10 = 'Acima de 10%'
}

export enum PurchasePriceRange {
  LESS_THAN_10K = 'Abaixo de R$ 10.000',
  BETWEEN_10K_20K = 'Entre R$ 10.000 e R$ 20.000',
  BETWEEN_20K_35K = 'Entre R$ 20.000 e R$ 35.000',
  BETWEEN_35K_50K = 'Entre R$ 35.000 e R$ 50.000',
  BETWEEN_50K_75K = 'Entre R$ 50.000 e R$ 75.000',
  BETWEEN_75K_100K = 'Entre R$ 75.000 e R$ 100.000',
  BETWEEN_100K_125K = 'Entre R$ 100.000 e R$ 125.000',
  BETWEEN_125K_150K = 'Entre R$ 125.000 e R$ 150.000',
  MORE_THAN = 'Acima de R$ 150.000'
}

export enum EvalStatus {
  CREATED = 'Aguardando RaioX',
  SCHEDULED = 'Agendada',
  WAITING = 'Aguardando envio',
  EVALUATING = 'Em captação',
  RETURNED_EVAL = 'Captação retornada',
  PRICING = 'Em precificação',
  NEGOTIATION = 'Em negociação',
  FAST_QUOTE = 'Em cotação rápida',
  BOUGHT = 'Captada',
  SOLD_FAST_QUOTE = 'Venda (cotação rápida)',
  FAST_QUOTE_WITH_OFFER = 'Em cotação rápida (com oferta)',
  EXPIRED = 'Expirada',
  CANCELLED = 'Cancelada',
  ARCHIVED = 'Arquivada',
  AWAITING_ENTRY = 'Aguardando Entrada'
}

export enum EvalHistoryItemType {
  CREATED = 'criou a captação',
  SCHEDULED = 'visita agendada',
  WAITING = 'aguardando envio',
  SENT_TO_EVAL = 'enviou para captação',
  STARTED = 'iniciou a captação',
  CLOSED_EVAL = 'finalizou a captação',
  CLOSED_PRICING = 'enviou para negociação',
  BOUGHT = 'comprou o veículo',
  CANCELLED = 'cancelou a captação',
  EXPIRED = 'A captação expirou automaticamente',
  UPDATED_INITIAL = 'atualizou a captação',
  UPDATED_EVALUATION = 'atualizou a captação',
  UPDATED_PRICING = 'atualizou o preço',
  UPDATED = 'atualizou a captação',
  RETURNED_EVAL = 'devolveu a captação',
  RETURNED_TO_PRICING = 'devolveu para precificação',
  REACTIVATED = 'ativou a captação',
  STARTED_FAST_QUOTE = 'enviou para cotação rápida',
  CHANGED_APPLICANT = 'alterou o solicitante',
  CHANGED_EVALUATOR = 'alterou o avaliador',
  CHANGED_EVAL_DEALER = 'alterou a unidade',
  ARCHIVED = 'arquivou a captação',
  VIEW = 'visualizou a captação',
  STARTED_PRICING = 'assumiu a precificação',
  ABANDONED_PRICING = 'abandonou a precificação',
  ESTIMATED_PRICE = 'estimou o preço',
  INTERVENTION_PRICE_ACCEPTED = 'intervenção no preço',
  EDITED_EVALUATION_COMMENT = 'editou uma observação'
}

export enum EvaluationDestination {
  TRADE_IN = 'Repasse',
  SHOWROOM = 'Showroom',
}

const listStatusIconsLabels = {
  CREATED: {
    icon: 'app-icon-edit',
    class: 'inactive',
    label: 'NÃO INICIADA',
    text: 'Cadastrado por {name} {time}',
  },
  SCHEDULED: {
    icon: 'app-icon-edit',
    class: 'sold',
    label: 'AGENDADA',
    text: 'Agendada para {scheduledDate}, {time}',
  },
  AWAITING_ENTRY: {
    icon: 'app-icon-board',
    class: 'waiting',
    label: 'AGUARDANDO ENTRADA',
    text: 'Aguardando entrada por {name} {time}',
  },
  WAITING: {
    icon: 'app-icon-edit',
    class: 'inactive',
    label: 'AGUARDANDO',
    text: 'Aguardando {time}',
  },
  SENT_TO_EVAL: {
    icon: 'app-icon-edit',
    class: 'inactive',
    label: 'NÃO INICIADA',
    text: 'Enviada para captação por {name} {time}',
  },
  REACTIVATED: {
    icon: 'app-icon-board',
    class: 'sold',
    label: 'REATIVADA',
    text: 'Reativada por {name} em {time}',
  },
  UPDATED_INITIAL: {
    icon: 'app-icon-edit',
    class: 'inactive',
    label: 'NÃO INICIADA',
    text: 'Atualizada por {name} em {time}',
  },
  STARTED: {
    icon: 'app-icon-board',
    class: 'sold',
    label: 'EM CAPTAÇÃO',
    text: 'Captação iniciada por {name} em {time}',
  },
  EVALUATING: {
    icon: 'app-icon-board',
    class: 'sold',
    label: 'EM CAPTAÇÃO',
    text: 'Captação iniciada por {name} em {time}',
  },
  UPDATED_EVALUATION: {
    icon: 'app-icon-board',
    class: 'sold',
    label: 'EM CAPTAÇÃO',
    text: 'Captação atualizada por {name} em {time}',
  },
  PRICING: {
    icon: 'app-icon-tag',
    class: 'active',
    label: 'EM PRECIFICAÇÃO',
    text: 'Precificação iniciada por {name} em {time}',
  },
  RETURNED_PRICING: {
    icon: 'app-icon-tag',
    class: 'returned_pricing',
    label: 'EM PRECIFICAÇÃO',
    text: 'Precificação devolvida por {name} em {time}',
  },
  CLOSED_EVAL: {
    icon: 'app-icon-tag',
    class: 'active',
    label: 'EM PRECIFICAÇÃO',
    text: 'Captação finalizada por {name} em {time}',
  },
  UPDATED_PRICING: {
    icon: 'app-icon-tag',
    class: 'sold',
    label: 'EM PRECIFICAÇÃO',
    text: 'Precificação atualizada em {time}',
  },
  NEGOTIATION: {
    icon: 'app-icon-negotiation',
    class: 'negotiation',
    label: 'EM NEGOCIAÇÃO',
    text: 'VALOR PROPOSTO',
  },
  UPDATED_NEGOTIATION: {
    icon: 'app-icon-negotiation',
    class: 'negotiation',
    label: 'EM NEGOCIAÇÃO',
    text: 'VALOR PROPOSTO ATUALIZADO',
  },
  CANCELLED: {
    icon: 'app-icon-edit',
    class: 'cancel',
    label: 'CANCELADA',
    text: 'Captação cancelada por {name} em {time}',
  },
  RETURNED_EVAL: {
    icon: 'app-icon-board',
    class: 'danger',
    label: 'EM CAPTAÇÃO',
    text: 'Devolvida por {name} em {time}',
  },
  CLOSED_PRICING: {
    icon: 'app-icon-negotiation',
    class: 'negotiation',
    label: 'EM NEGOCIAÇÃO',
    text: 'Enviado para negociação por {name} em {time}',
  },
  BOUGHT: {
    icon: 'app-icon-tag',
    class: 'active',
    label: 'COMPROU',
    text: 'Comprou em {time}',
  },
  TRADE_IN: {
    icon: 'app-icon-repass',
    class: 'active',
    label: 'REPASSE',
    text: 'Comprou em {time}',
  },
  SHOWROOM: {
    icon: 'app-icon-showroom',
    class: 'showroom',
    label: 'SHOWROOM',
    text: 'Comprou em {time}',
  },
  ARCHIVED: {
    icon: 'material-icons',
    class: 'archived',
    label: 'ARQUIVADO',
    text: 'Arquivado por {name} em {time}',
    materialIcon: 'query_builder',
  },
  SOLD_FAST_QUOTE: {
    icon: 'app-icon-repass',
    class: 'active',
    label: 'VENDIDO VIA COTAÇÃO RÁPIDA',
    text: 'Vendido via cotação rápida'
  },
  FAST_QUOTE: {
    icon: 'app-icon-fast-lightning',
    class: 'fast-quote',
    label: 'EM COTAÇÃO RÁPIDA',
    text: 'Cotação rápida'
  },
  FAST_QUOTE_WITH_OFFER: {
    icon: 'app-icon-fast-lightning',
    class: 'fast-quote',
    label: 'EM COTAÇÃO RÁPIDA',
    text: 'Cotação rápida'
  },
  EXPIRED: {
    icon: 'material-icons',
    class: 'inactive',
    label: 'EXPIRADO',
    text: 'Expirada automaticamente {time}',
    materialIcon: 'query_builder',
  },
};

export function buildEvaluationStatus(
  vehicle: EvaluationVehicle,
  checkStatusVehicle: boolean = true
) {

  if (vehicle.lastHistoryItem === undefined) {
    return listStatusIconsLabels[vehicle.status];
  }
  const {
    status,
    lastHistoryItem: { type },
    destination,
  } = vehicle;
  if (status === StatusEvaluation.BOUGHT && EvaluationDestination[destination]) {
    return listStatusIconsLabels[destination];
  }

  if (status === StatusEvaluation.ARCHIVED) {
    return listStatusIconsLabels[StatusEvaluation.ARCHIVED];
  }

  if (status === StatusEvaluation.AWAITING_ENTRY) {
    return listStatusIconsLabels[StatusEvaluation.AWAITING_ENTRY];
  }

  if (checkStatusVehicle) {
    return listStatusIconsLabels[status] || listStatusIconsLabels[type];
  }

  return listStatusIconsLabels[type] || listStatusIconsLabels[status];
}

@Injectable({
  providedIn: "root",
})
export class EvalReasonArchiveHelper {
  reasonArchive?: string;
}

export class ReasonsArchivesLabel {
  static allTypes = ['ONLINE_ASSESSMENT',
    'PRECAUTIONARY_REPROVED',
    'CUSTOMER_GAVE_UP',
    'PURCHASED_FROM_ANOTHER_DEALER',
    'CREDIT_REPROVED',
    'BUSINESS_NO_EXCHANGE',
    'PRICING',
    'PRODUCT_UNAVAILABLE',
    'NO_RETURN',
    'THIRD_PARTY_SALE',];

  static getLabel(type: string): string {
    switch (type) {
      case 'CUSTOMER_GAVE_UP': return 'Cliente desistiu';
      case 'CREDIT_REPROVED': return 'Crédito reprovado';
      case 'NO_RETURN': return 'Sem retorno';
      case 'PRICING': return 'Precificação';
      case 'THIRD_PARTY_SALE': return 'Venda terceiros';
      case 'PRECAUTIONARY_REPROVED': return 'Cautelar reprovada';
      case 'PRODUCT_UNAVAILABLE': return 'Produto indisponível';
      case 'BUSINESS_NO_EXCHANGE': return 'Deu negócio - sem troca';
      case 'PURCHASED_FROM_ANOTHER_DEALER': return 'Comprou em outra concessionária';
      case 'ONLINE_ASSESSMENT': return 'Avaliação Online';
      default: return type;
    }
  }
}

export enum NegotiationStatus {
  HOT = "HOT",
  COLD = "COLD",
  TEPID = "TEPID",
  DEAL_DONE = "DEAL_DONE",

  //cpfl
  SOLD = "SOLD",
  CONDITIONAL = "CONDITIONAL",
  BLOCKED = "BLOCKED",
  AVAILABLE_FOR_SALE = "AVAILABLE_FOR_SALE",
  AUCTION_IN_PROGRESS = "AUCTION_IN_PROGRESS",
  ALLOCATION = "ALLOCATION"
}

export class FastQuoteStatus {
  static allTypes = ['WITH_OFFER', 'NO_OFFER', 'EXPIRED_WITH_OFFER', 'EXPIRED_NO_OFFER'];
  static allTypesOffer = ['WITH_OFFER', 'EXPIRED_WITH_OFFER'];
  static allTypeNoOffer = ['NO_OFFER', 'EXPIRED_NO_OFFER'];

  static getLabel(string) {
    switch (string) {
      case 'WITH_OFFER':
        return "Com oferta";
      case 'NO_OFFER':
        return "Sem oferta";
      case 'EXPIRED_WITH_OFFER':
        return "Com oferta (finalizada)";
      case 'EXPIRED_NO_OFFER':
        return "Sem oferta (finalizada)";
    }
  }
}

export class NegotiationStatusTypes {
  static allTypes = ['COLD', 'HOT', 'TEPID', 'NO_STATUS', 'DEAL_DONE', 'SOLD', 'CONDITIONAL', 'BLOCKED', 'AVAILABLE_FOR_SALE', 'AUCTION_IN_PROGRESS', 'ALLOCATION'];

  static getLabel(string) {
    switch (string) {
      case 'COLD':
        return "Frio";
      case 'HOT':
        return 'Quente';
      case 'TEPID':
        return "Morno";
      case 'SOLD':
        return "Vendido";
      case 'CONDITIONAL':
        return 'Condicional'
      case 'BLOCKED':
        return 'Bloqueado';
      case 'AVAILABLE_FOR_SALE':
        return 'Disponível para Venda';
      case 'NO_STATUS':
        return 'Sem Status';
      case 'DEAL_DONE':
        return 'Deu Negócio';
      case 'AUCTION_IN_PROGRESS':
        return 'Leilão em progresso';
      case 'ALLOCATION':
        return 'Em loteamento'
    }
  }
}

export class DossierHelper {
  dossier: VehicleFullDossier;
  fullDossierDate?: Date;
}

export class EvalEvolution {
  request: Date;
  requestUserId: string;
  requestUsername: string;
  requestUserProfileName?: string;
  requestUserProfileColor?: string;

  evaluationIni?: Date;
  evaluationEnd?: Date;
  evaluationUserId?: string;
  evaluationUsername?: string;
  evaluationUserProfileName?: string;
  evaluationUserProfileColor?: string;

  pricingIni: Date;
  pricingEnd: Date;
  pricingUserId: string;
  pricingUsername: string;
  pricingUserProfileName?: string;
  pricingUserProfileColor?: string;

  negotiationIni: Date;
  negotiationEnd: Date;
  negotiationUserId: string;
  negotiationUsername: string;
  negotiationUserProfileName?: string;
  negotiationUserProfileColor?: string;

  result: Date;
  resultUserId: string;
  resultUsername: string;
  resultUserProfileName?: string;
  resultUserProfileColor?: string;

  awaitingEntryAt: Date;
  awaitingEntryUserId: string;
  awaitingEntryUsername: string;
  awaitingEntryUserProfileName?: string;
  awaitingEntryUserProfileColor?: string;

}

export class EvalReportItem {
  id: string;
  externalKey: any;
  fastQuoteId: string;
  numberPlate: string;
  makeName: string;
  imgUrl: string;
  modelName: string;
  trimName: string;
  manufactureYear: number;
  modelYear: number;
  status: EvalStatus;
  createdAt: Date;
  fipePrice: number;
  price: number;
  priceCent?: number;
  buyPrice: number;
  evalDealerName: string;
  cityName: string;
  uf: string;
  currentEvaluatorName: string;
  sellerName: string;
  pricingName: string;
  pricingStartedAt?: Date;
  pricingEndedAt?: Date;
  pricingDurationMinutes?: number;
  lastDeskPrice: number;
  sellerManagerPriceUpdate: string;
  score: number;
  destination: EvaluationDestination;
  type: string;
  reason: string;
  hasFullDossier: boolean;
  oemDealerName: string;
  regionalName: string;
  uniqueImpressions: number;
  uniqueViews: number;
  bids: number;
}


export class EvalReportFilter extends EvaluationVehicleFilter {

  evalIds: string[];
  constructor() {
    super();
    this.evalIds = [];
  }
}

export class Axle {
  position: number;
  tireCount: 2 | 4;
  possibleTireCount?: 2 | 4;
}

export class ReportItemHelper {
  id: string;
  name: string;
  reportItem: EquipmentReport;
}

export class TireReport {
  axlePosition: number;
  tirePosition: number;
  color?: string;
  classification: 'GOOD' | 'REGULAR' | 'BAD' | 'NON_EXISTENT';
}

export class EvalImageOptionHelper {
  id: string;
  name: string;
  url: string;
}

export class OwnerPhoneConfirmationRequest{
  phoneNumber: string;
  oemDealerId: string;
  evalDealerId: string;
  numberPlate: string;
}

export interface EvalVehicleLookupTrimOption {
  id: string;
  name: string;
  fipeId?: string;
  yearsModel?: string | number;
}

export interface EvalVehicleLookupResponse {
  success: boolean;
  status?: string;
  vehicleDetails?: EvaluationVehicle;
  availableTrims?: EvalVehicleLookupTrimOption[];
}

export enum EvalVehicleUpdateStatus {
  OWNER_CONFIRMATION_REQUIRED = "Obrigatório confirmar o telefone do proprietário",
  INVALID_PHONE_CONFIRMATION_CODE = "Código de confirmação inválido",
  OWNER_PHONE_DIVERGES_FROM_CONFIRMATION = "Telefone divergente do utilizado na confirmação",
  USER_HAS_NO_DEALER = "Usuário não está vinculado a uma unidade",
  NUMBERPLATE_ALREADY_EXISTS = "Um veículo com esta placa já está cadastrado.",
  ALREADY_EXISTS = "Já existe uma avaliação em andamento para este veículo"
}

export enum MarketModality {
  GENERAL = "Geral",
  SOLD_FAST_QUOTE_ONLY = "Com cotações rápidas vendidas",
  WITHOUT_FAST_QUOTE = "Sem cotações rápidas"
}
